import React, { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { readReviewNotes, useChatReviewStorage } from '../useChatReviewStorage';

import ChatReviewNavigation from './ChatReviewNavigation';

export default function ChatReviewNotes({ chatId }: { chatId?: string }) {
  const { notes, error, save, clear } = useChatReviewStorage();
  const [draft, setDraft] = useState<{ chatId: string; value: string }>();
  const [confirmClear, setConfirmClear] = useState(false);
  const [exportError, setExportError] = useState('');
  const count = Object.keys(notes).length;
  const download = () => {
    try {
      const data = Object.entries(readReviewNotes()).map(([id, note]) => ({ chatId: Number(id), note }));
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'trip-planner-review-notes.json';
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportError('');
    } catch {
      setExportError('Could not export notes. Your saved notes have not been changed.');
    }
  };
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Review notes</Typography>
      {chatId && (
        <TextField
          placeholder="Observations, open codes, possible eval cases…"
          inputProps={{ 'aria-label': 'Review notes' }}
          multiline
          minRows={5}
          fullWidth
          value={draft?.chatId === chatId ? draft.value : (notes[chatId] ?? '')}
          onChange={(event) => {
            const value = event.target.value;
            setDraft({ chatId, value });
            if (save(chatId, value)) {
              setDraft(undefined);
            }
          }}
        />
      )}
      {chatId && <ChatReviewNavigation chatId={chatId} />}
      <Typography variant="caption" color="text.secondary">
        Saved automatically in this browser only.
      </Typography>
      {(error || exportError) && <Alert severity="error">{error || exportError}</Alert>}
      <Stack direction="row" gap={1}>
        <Button
          size="small"
          variant="text"
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          disabled={!count || Boolean(error)}
          onClick={download}
        >
          Download JSON
        </Button>
        <Button
          size="small"
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          disabled={!count}
          onClick={() => setConfirmClear(true)}
        >
          Clear notes
        </Button>
      </Stack>
      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>Clear all review notes?</DialogTitle>
        <DialogContent>
          This removes notes for all {count} chats from this browser. Download a JSON copy first if you want to keep
          them.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (clear()) {
                setDraft(undefined);
                setConfirmClear(false);
              }
            }}
          >
            Clear all notes
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
